"use client";

/* eslint-disable complexity */
import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { IconButton } from "@faire/web--source/slate/IconButton/IconButton";
import type { IconButtonVariants } from "@faire/web--source/slate/IconButton/types";
import { Chevron } from "@faire/web--source/slate/icons/Chevron";
import { Flex } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { SpacerValueOrKey } from "@faire/web--source/slate/spacing/getSpacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import {
  isResponsiveProp,
  ResponsiveProp,
} from "@faire/web--source/slate/Theme/ResponsiveProp";
import { ResponsiveValues } from "@faire/web--source/slate/Theme/ResponsiveValues";
import { cn } from "@faire/web--source/tools/cn";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { Viewport } from "@faire/web--source/ui/hooks/viewport-utils/viewport-utils";
import {
  EmblaCarouselType,
  EmblaOptionsType,
  EmblaPluginType,
} from "embla-carousel";
import Autoplay, { AutoplayOptionsType } from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { CarouselDots } from "./__internal__/CarouselDots";
import { CTATile, CTATileData, shouldAddCTATile } from "./__internal__/CTATile";
// eslint-disable-next-line import/no-unassigned-import
import "./__internal__/native-styles.css";

export const ITEM_GAP_DEFAULT: ResponsiveValues<number> = {
  mobileAndAbove: 16,
  tabletAndAbove: 16,
};
export const ITEM_PER_SLIDE_DEFAULT: ResponsiveValues<number> = {
  mobileAndAbove: 2,
  tabletAndAbove: 3,
  desktopAndAbove: 5,
};
const PEEK_NEXT_PERCENT_DEFAULT: ResponsiveValues<number> = {
  mobileAndAbove: 10,
  tabletAndAbove: 0,
  desktopAndAbove: 0,
};
const PEEK_PREVIOUS_PERCENT_DEFAULT: ResponsiveValues<number> = {
  mobileAndAbove: 0,
  tabletAndAbove: 0,
  desktopAndAbove: 0,
};
const DRAG_FREE_DEFAULT: ResponsiveValues<boolean> = {
  mobileAndAbove: true,
  tabletAndAbove: false,
  desktopAndAbove: false,
};
const LOOP_DEFAULT: ResponsiveValues<boolean> = {
  mobileAndAbove: false,
  tabletAndAbove: true,
  desktopAndAbove: true,
};
const SHOW_NAVIGATION_ARROWS_DEFAULT: ResponsiveValues<boolean> = {
  mobileAndAbove: false,
  tabletAndAbove: true,
};

type ControlsVariant = "primary" | "secondary";

type ResponsiveValuesGenerated<T> = {
  mobileAndAbove: T;
  tabletAndAbove: T;
  desktopAndAbove: T;
  xLargeAndAbove: T;
  xxLargeDesktop: T;
};

export interface ICarouselProps<T> {
  items: ReadonlyArray<T>;
  renderItem: (item: T, index: number, slideIndex?: number) => React.ReactNode;
  itemsPerSlide?: ResponsiveProp<number>;
  loop?: ResponsiveProp<boolean>;
  dragFree?: ResponsiveProp<boolean>;
  overScanSlides?: ResponsiveProp<number>;
  peekNextPercent?: ResponsiveProp<number>;
  peekPreviousPercent?: ResponsiveProp<number>;
  itemGap?: ResponsiveProp<number>;
  emblaOptions?: EmblaOptionsType;
  arrowColorOrVariant?: ControlsVariant | string;
  dotsVariant?: ControlsVariant;
  shouldShowDots?: ResponsiveProp<boolean>;
  dotsOffsetY?: ResponsiveProp<SpacerValueOrKey>;
  onVisible?: (items: T[]) => void;
  ctaTileData?: CTATileData;
  className?: string;
  showNavigationArrows?: ResponsiveProp<boolean>;
  /**
   * If true, the navigation arrows will be shown but disabled when the carousel is at the beginning or end.
   * Otherwise, hide the arrows entirely when disabled.
   * Note: this is only enabled on the homepage carousels as part of #f-homepage-top-of-fold-recs
   */
  shouldShowDisabledNavigationArrows?: boolean;
  arrowOffsetX?: number;
  /**
   * if true, the carousel will be free-flowing and not snap to fixed number of slides
   */
  freeFlow?: boolean;
  /**
   * if true, the carousel will be only functional after view
   */
  attachRefOnView?: boolean;
  /**
   * Whether the carousel will automatically switch between slides
   */
  autoplay?: ResponsiveProp<boolean>;
  autoplayOptions?: AutoplayOptionsType;
  plugins?: EmblaPluginType[];
  shouldOverEstimateFlagOnSSR?: boolean;
}

type GetInitialRenderFlagsArg = {
  numItems: number;
  itemsPerSlideGenerated: ResponsiveValuesGenerated<number>;
  peekNextPercentGenerated: ResponsiveValuesGenerated<number>;
  peekPreviousPercentGenerated: ResponsiveValuesGenerated<number>;
};

const getInitialRenderFlagsForViewport = (
  key: keyof ResponsiveValuesGenerated<number>,
  {
    numItems,
    itemsPerSlideGenerated,
    peekNextPercentGenerated,
    peekPreviousPercentGenerated,
  }: GetInitialRenderFlagsArg
) =>
  [
    ...Array(
      itemsPerSlideGenerated[key] + (peekNextPercentGenerated[key] > 0 ? 1 : 0)
    ).keys(),
    peekPreviousPercentGenerated[key] > 0 ? numItems - 1 : undefined,
  ].filter(isNotUndefined);

/**
 * Gets the indicies of the slides that should be rendered initially
 */
const getInitialRenderFlags = (
  viewport: Partial<Viewport>,
  arg: GetInitialRenderFlagsArg,
  isSSR: boolean,
  shouldOverEstimateFlagOnSSR: boolean
) => {
  if (isSSR && shouldOverEstimateFlagOnSSR && viewport.isDesktopAndAbove) {
    return getInitialRenderFlagsForViewport("xLargeAndAbove", arg);
  }
  if (viewport.isXXLargeDesktop) {
    return getInitialRenderFlagsForViewport("xxLargeDesktop", arg);
  } else if (viewport.isXLargeAndAbove) {
    return getInitialRenderFlagsForViewport("xLargeAndAbove", arg);
  } else if (viewport.isDesktopAndAbove) {
    return getInitialRenderFlagsForViewport("desktopAndAbove", arg);
  } else if (viewport.isTabletAndAbove) {
    return getInitialRenderFlagsForViewport("tabletAndAbove", arg);
  } else {
    return getInitialRenderFlagsForViewport("mobileAndAbove", arg);
  }
};

const shouldAddCTATileWithViewport = <T,>(
  arr: ReadonlyArray<T>,
  data: CTATileData | undefined,
  itemsPerSlide: ResponsiveValuesGenerated<number>,
  viewport: Partial<Viewport>
) => {
  let itemsPerSlideWithViewport = itemsPerSlide.xxLargeDesktop;

  if (viewport.isXLargeDesktop) {
    itemsPerSlideWithViewport = itemsPerSlide.xLargeAndAbove;
  } else if (viewport.isDesktop) {
    itemsPerSlideWithViewport = itemsPerSlide.desktopAndAbove;
  } else if (viewport.isTablet) {
    itemsPerSlideWithViewport = itemsPerSlide.tabletAndAbove;
  } else if (viewport.isMobile) {
    itemsPerSlideWithViewport = itemsPerSlide.mobileAndAbove;
  }
  return shouldAddCTATile(arr, data, itemsPerSlideWithViewport);
};

const generateResponsiveValuesFromProps = <T,>(prop: ResponsiveProp<T>) => {
  const mobileAndAbove = isResponsiveProp(prop) ? prop.mobileAndAbove : prop;
  const tabletAndAbove =
    (isResponsiveProp(prop) ? prop.tabletAndAbove : prop) ?? mobileAndAbove;
  const desktopAndAbove =
    (isResponsiveProp(prop) ? prop.desktopAndAbove : prop) ?? tabletAndAbove;
  const xLargeAndAbove =
    (isResponsiveProp(prop) ? prop.xLargeAndAbove : prop) ?? desktopAndAbove;
  const xxLargeDesktop =
    (isResponsiveProp(prop) ? prop.xxLargeDesktop : prop) ?? xLargeAndAbove;
  return {
    mobileAndAbove,
    tabletAndAbove,
    desktopAndAbove,
    xLargeAndAbove,
    xxLargeDesktop,
  };
};

export const Carousel = <T,>(props: ICarouselProps<T>) => {
  const {
    items,
    renderItem,
    itemsPerSlide = ITEM_PER_SLIDE_DEFAULT,
    itemGap = ITEM_GAP_DEFAULT,
    peekNextPercent = PEEK_NEXT_PERCENT_DEFAULT,
    peekPreviousPercent = PEEK_PREVIOUS_PERCENT_DEFAULT,
    loop = LOOP_DEFAULT,
    dragFree = DRAG_FREE_DEFAULT,
    arrowColorOrVariant,
    dotsVariant = "primary",
    emblaOptions,
    onVisible,
    ctaTileData,
    shouldShowDots,
    dotsOffsetY,
    showNavigationArrows = SHOW_NAVIGATION_ARROWS_DEFAULT,
    shouldShowDisabledNavigationArrows,
    arrowOffsetX,
    freeFlow,
    className,
    attachRefOnView,
    autoplay,
    autoplayOptions,
    plugins = [],
    shouldOverEstimateFlagOnSSR = false,
  } = props;
  const viewport = useViewport();
  const { strictLocalize } = useStrictLocalization();

  // Generate props for each screen size
  const itemsPerSlideGenerated =
    generateResponsiveValuesFromProps(itemsPerSlide);
  const itemGapGenerated = generateResponsiveValuesFromProps(itemGap);
  const peekNextPercentGenerated =
    generateResponsiveValuesFromProps(peekNextPercent);
  const peekPreviousPercentGenerated =
    generateResponsiveValuesFromProps(peekPreviousPercent);
  const loopGenerated = generateResponsiveValuesFromProps(loop);
  const dragFreeGenerated = generateResponsiveValuesFromProps(dragFree);
  const shouldShowDotsGenerated =
    generateResponsiveValuesFromProps(shouldShowDots);
  const showNavigationArrowsGenerated =
    generateResponsiveValuesFromProps(showNavigationArrows);

  // Get current screen size value to see if we should show dots
  const shouldShowDotsResponsive = useResponsiveValue(shouldShowDotsGenerated);
  const showNavigationArrowsResponsive = useResponsiveValue(
    showNavigationArrowsGenerated
  );

  const autoplayGenerated = generateResponsiveValuesFromProps(autoplay);
  const shouldAutoplay = useResponsiveValue(autoplayGenerated);

  const hasCTA = shouldAddCTATileWithViewport(
    items,
    ctaTileData,
    itemsPerSlideGenerated,
    viewport
  );

  const defaultEmblaOptionsMobile: EmblaOptionsType = {
    align: peekPreviousPercentGenerated.mobileAndAbove > 0 ? "center" : "start",
    dragFree: dragFreeGenerated.mobileAndAbove,
    loop: loopGenerated.mobileAndAbove,
    slidesToScroll: itemsPerSlideGenerated.mobileAndAbove,
  };

  const defaultEmblaOptionsTablet: EmblaOptionsType = {
    align: peekPreviousPercentGenerated.tabletAndAbove > 0 ? "center" : "start",
    loop: ctaTileData ? false : loopGenerated.tabletAndAbove,
    dragFree: dragFreeGenerated.tabletAndAbove,
    slidesToScroll: itemsPerSlideGenerated.tabletAndAbove,
  };

  const defaultEmblaOptionsDesktopAndAbove: EmblaOptionsType = {
    align:
      peekPreviousPercentGenerated.desktopAndAbove > 0 ? "center" : "start",
    loop: ctaTileData ? false : loopGenerated.desktopAndAbove,
    dragFree: dragFreeGenerated.desktopAndAbove,
    slidesToScroll: itemsPerSlideGenerated.desktopAndAbove,
  };

  const defaultEmblaOptionsXLargeAndAbove: EmblaOptionsType = {
    align: peekPreviousPercentGenerated.xLargeAndAbove > 0 ? "center" : "start",
    loop: ctaTileData ? false : loopGenerated.xLargeAndAbove,
    dragFree: dragFreeGenerated.xLargeAndAbove,
    slidesToScroll: itemsPerSlideGenerated.xLargeAndAbove,
  };

  const defaultEmblaOptionsXXLargeDesktop: EmblaOptionsType = {
    align: peekPreviousPercentGenerated.xxLargeDesktop > 0 ? "center" : "start",
    loop: ctaTileData ? false : loopGenerated.xxLargeDesktop,
    dragFree: dragFreeGenerated.xxLargeDesktop,
    slidesToScroll: itemsPerSlideGenerated.xxLargeDesktop,
  };

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(
    showNavigationArrowsResponsive
  );
  const [nextBtnEnabled, setNextBtnEnabled] = useState(
    showNavigationArrowsResponsive
  );

  const isSSR = useIsSSR();
  const [slideRenderFlags, setSlideRenderFlags] = useState<Set<number>>(() => {
    return new Set(
      getInitialRenderFlags(
        viewport,
        {
          numItems: items.length,
          itemsPerSlideGenerated,
          peekNextPercentGenerated,
          peekPreviousPercentGenerated,
        },
        isSSR,
        shouldOverEstimateFlagOnSSR
      )
    );
  });

  //If the native navigation is disabled, we should disable the wheel gestures as well
  //Since it will make the carousel scrollable even if it's not needed,
  //which will make user interaction less smooth, and stuck the carousel in a weird state
  const isNavigationDisabled = !prevBtnEnabled && !nextBtnEnabled;

  const [emblaRef, emblaAPI] = useEmblaCarousel(
    {
      containScroll: "trimSnaps",
      breakpoints: {
        [responsiveMediaQueries.mobile({}).replace("@media", "")]:
          defaultEmblaOptionsMobile,
        [responsiveMediaQueries.tablet({}).replace("@media", "")]:
          defaultEmblaOptionsTablet,
        [responsiveMediaQueries.desktopAndAbove({}).replace("@media", "")]:
          defaultEmblaOptionsDesktopAndAbove,
        [responsiveMediaQueries.xLargeAndAbove({}).replace("@media", "")]:
          defaultEmblaOptionsXLargeAndAbove,
        [responsiveMediaQueries.xxlarge({}).replace("@media", "")]:
          defaultEmblaOptionsXXLargeDesktop,
      },
      ...emblaOptions,
    },
    [
      ...(isNavigationDisabled ? [] : [WheelGesturesPlugin()]),
      ...(shouldAutoplay ? [Autoplay(autoplayOptions)] : []),
      ...plugins,
    ]
  );
  const [carouselViewed, setCarouselViewed] = useState(false);
  const slideViewFlags = useRef<Set<number>>(new Set());

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const onVisibleRef = useRef(onVisible);
  onVisibleRef.current = onVisible;

  const sendViewEvent = useCallback((emblaApi: EmblaCarouselType) => {
    const newItemsInView: Set<number> = new Set();
    emblaApi.slidesInView().forEach((value) => {
      if (!slideViewFlags.current.has(value)) {
        slideViewFlags.current.add(value);
        newItemsInView.add(value);
      }
    });
    const itemsInView: T[] = [];
    if (newItemsInView.size > 0) {
      newItemsInView.forEach((value) => {
        const item = itemsRef.current[value];
        if (item !== undefined) {
          itemsInView.push(item);
        }
      });
      onVisibleRef.current?.(itemsInView);
    }
  }, []);

  const viewRef = useDoActionWhenVisible(() => {
    setCarouselViewed(true);
  });

  const sendInitialViewEvent = useCallback(
    (emblaApi: EmblaCarouselType) => {
      sendViewEvent(emblaApi);
      emblaApi.off("slidesInView", sendInitialViewEvent);
    },
    [sendViewEvent]
  );

  const onSlideInViewChange = useCallback((emblaApi: EmblaCarouselType) => {
    setSlideRenderFlags((state) => {
      if (state.size === itemsRef.current.length) {
        emblaApi.off("slidesInView", onSlideInViewChange);
        return state;
      }
      let addedFlag = false;
      emblaApi.slidesInView().forEach((value) => {
        if (!state.has(value)) {
          state.add(value);
          addedFlag = true;
        }
      });
      if (addedFlag) {
        return new Set([...state]);
      }
      return state;
    });
  }, []);

  const onSettle = useCallback(
    (emblaApi: EmblaCarouselType) => {
      setHasScrolled(true);
      if (slideViewFlags.current.size === itemsRef.current.length) {
        emblaApi.off("settle", onSettle);
        return;
      }
      sendViewEvent(emblaApi);
    },
    [sendViewEvent]
  );

  const onSelect = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (shouldShowDotsResponsive) {
        setSelectedIndex(emblaApi.selectedScrollSnap());
      }
      setPrevBtnEnabled(emblaApi.canScrollPrev());
      setNextBtnEnabled(emblaApi.canScrollNext());
    },
    [shouldShowDotsResponsive]
  );

  const onReInit = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (!freeFlow) {
        emblaApi.off("slidesInView", onSlideInViewChange);
        emblaApi.on("slidesInView", onSlideInViewChange);
      }
      slideViewFlags.current.clear();
      sendViewEvent(emblaApi);
      onSelect(emblaApi);
    },
    [freeFlow, onSelect, onSlideInViewChange, sendViewEvent]
  );

  const scrollPrev = useCallback(() => {
    emblaAPI?.scrollPrev();
    setHasScrolled(true);
  }, [emblaAPI]);

  const scrollNext = useCallback(() => {
    emblaAPI?.scrollNext();
    setHasScrolled(true);
  }, [emblaAPI]);

  const scrollTo = useCallback(
    (index: number) => {
      emblaAPI?.scrollTo?.(index);
      setHasScrolled(true);
    },
    [emblaAPI]
  );

  const { arrowVariant, arrowColor, arrowColorHover } = useMemo((): {
    arrowVariant: IconButtonVariants;
    arrowColor: string | undefined;
    arrowColorHover: string | undefined;
  } => {
    if (arrowColorOrVariant === "primary") {
      return {
        arrowVariant: "primary",
        arrowColor: Core.icon.inverse,
        arrowColorHover: Core.icon.inverse,
      };
    }
    if (arrowColorOrVariant === "secondary") {
      return {
        arrowVariant: "secondary",
        arrowColor: Core.icon.default,
        arrowColorHover: Core.icon.default,
      };
    }
    return {
      arrowVariant: "tertiary",
      arrowColor: arrowColorOrVariant ?? Core.icon.default,
      arrowColorHover: Core.icon.default,
    };
  }, [arrowColorOrVariant]);

  const [shouldShowPrevButton, shouldShowNextButton] = useMemo(() => {
    const shouldShowPrevButton =
      showNavigationArrowsResponsive &&
      (shouldShowDisabledNavigationArrows
        ? // we want to hide the previous button if we are still on the first set of tiles
          hasScrolled || prevBtnEnabled
        : prevBtnEnabled);

    const shouldShowNextButton =
      showNavigationArrowsResponsive &&
      (shouldShowDisabledNavigationArrows
        ? hasScrolled || nextBtnEnabled
        : nextBtnEnabled);

    return [shouldShowPrevButton, shouldShowNextButton];
  }, [
    prevBtnEnabled,
    nextBtnEnabled,
    showNavigationArrowsResponsive,
    shouldShowDisabledNavigationArrows,
    hasScrolled,
  ]);

  useEffect(() => {
    if (!emblaAPI || !carouselViewed) {
      return;
    }
    if (emblaAPI.slidesInView().length === 0) {
      emblaAPI.on("slidesInView", sendInitialViewEvent);
    } else {
      sendViewEvent(emblaAPI);
    }
  }, [carouselViewed, emblaAPI, sendInitialViewEvent, sendViewEvent]);

  useEffect(() => {
    if (!emblaAPI) {
      return;
    }
    onSelect(emblaAPI);
    if (!freeFlow) {
      emblaAPI.on("slidesInView", onSlideInViewChange);
    }
    emblaAPI.on("settle", onSettle);
    emblaAPI.on("select", onSelect);
    emblaAPI.on("reInit", onReInit);
  }, [emblaAPI, onSlideInViewChange, onSelect, onSettle, onReInit, freeFlow]);

  const renderPreviousButton = () => {
    return shouldShowPrevButton ? (
      <IconButton
        onClick={scrollPrev}
        data-test-id="btn-previous"
        aria-label={strictLocalize({
          defaultMessage: "Previous Carousel Slide",
          description: {
            text: "Aria label for the previous carousel slide button",
          },
        })}
        variant={arrowVariant}
        aria-disabled={!prevBtnEnabled}
        disabled={!prevBtnEnabled}
        className={cn("f_c_icon_button", "f_c_icon_button_previous")}
        size="small"
      >
        <Chevron direction="left" />
      </IconButton>
    ) : null;
  };

  const renderNextButton = () => {
    return shouldShowNextButton ? (
      <IconButton
        onClick={scrollNext}
        data-test-id="btn-next"
        aria-label={strictLocalize({
          defaultMessage: "Next Carousel Slide",
          description: {
            text: "Aria label for the next carousel slide button",
          },
        })}
        variant={arrowVariant}
        aria-disabled={!nextBtnEnabled}
        disabled={!nextBtnEnabled}
        className={cn("f_c_icon_button", "f_c_icon_button_next")}
        size="small"
      >
        <Chevron direction="right" />
      </IconButton>
    ) : null;
  };

  const renderDots = () => {
    if (!shouldShowDotsResponsive || !emblaAPI) {
      return null;
    }
    return (
      <>
        <Spacer size={dotsOffsetY ?? "5x"} />
        <CarouselDots
          onClick={(index) => {
            scrollTo(index);
          }}
          currentImageIndex={selectedIndex}
          numValidImages={emblaAPI.scrollSnapList().length}
          dotSize="large"
          colorVariant={dotsVariant === "secondary" ? "light" : "dark"}
        />
      </>
    );
  };

  const slideStylesWidth = freeFlow
    ? "f_c_slide_width_free_flow"
    : ["f_c_slide_width_fixed_base", "f_c_slide_width_fixed_variables"];
  const slideClassName = cn(slideStylesWidth, "f_c_slide_gap");

  const shouldAttachEmblaRef = !attachRefOnView || carouselViewed;

  const vars: Record<string, string | undefined> = {
    // Arrow variables
    ["--f-c-arrow-color"]: arrowColor,
    ["--f-c-arrow-color-hover"]: arrowColorHover,
    ["--f-c-arrow-offset-x"]: (arrowOffsetX ?? "8") + "px",
    // Item per slide variables
    ["--f-c-item-per-slide-xxlarge"]:
      itemsPerSlideGenerated.xxLargeDesktop.toString(),
    ["--f-c-item-per-slide-xlarge"]:
      itemsPerSlideGenerated.xLargeAndAbove.toString(),
    ["--f-c-item-per-slide-desktop"]:
      itemsPerSlideGenerated.desktopAndAbove.toString(),
    ["--f-c-item-per-slide-tablet"]:
      itemsPerSlideGenerated.tabletAndAbove.toString(),
    ["--f-c-item-per-slide-mobile"]:
      itemsPerSlideGenerated.mobileAndAbove.toString(),
    // Item gap variables
    ["--f-c-item-gap-xxlarge"]: itemGapGenerated.xxLargeDesktop + "px",
    ["--f-c-item-gap-xlarge"]: itemGapGenerated.xLargeAndAbove + "px",
    ["--f-c-item-gap-desktop"]: itemGapGenerated.desktopAndAbove + "px",
    ["--f-c-item-gap-tablet"]: itemGapGenerated.tabletAndAbove + "px",
    ["--f-c-item-gap-mobile"]: itemGapGenerated.mobileAndAbove + "px",
    // Peek next variables
    ["--f-c-peek-next-xxlarge"]: (
      (peekNextPercentGenerated.xxLargeDesktop ?? 0) / 100
    ).toString(),
    ["--f-c-peek-next-xlarge"]: (
      (peekNextPercentGenerated.xLargeAndAbove ?? 0) / 100
    ).toString(),
    ["--f-c-peek-next-desktop"]: (
      (peekNextPercentGenerated.desktopAndAbove ?? 0) / 100
    ).toString(),
    ["--f-c-peek-next-tablet"]: (
      (peekNextPercentGenerated.tabletAndAbove ?? 0) / 100
    ).toString(),
    ["--f-c-peek-next-mobile"]: (
      (peekNextPercentGenerated.mobileAndAbove ?? 0) / 100
    ).toString(),
    // Peek previous variables
    ["--f-c-peek-prev-xxlarge"]: (
      (peekPreviousPercentGenerated.xxLargeDesktop ?? 0) / 100
    ).toString(),
    ["--f-c-peek-prev-xlarge"]: (
      (peekPreviousPercentGenerated.xLargeAndAbove ?? 0) / 100
    ).toString(),
    ["--f-c-peek-prev-desktop"]: (
      (peekPreviousPercentGenerated.desktopAndAbove ?? 0) / 100
    ).toString(),
    ["--f-c-peek-prev-tablet"]: (
      (peekPreviousPercentGenerated.tabletAndAbove ?? 0) / 100
    ).toString(),
    ["--f-c-peek-prev-mobile"]: (
      (peekPreviousPercentGenerated.mobileAndAbove ?? 0) / 100
    ).toString(),
    // Has peek next variables
    ["--f-c-has-peek-next-xxlarge"]: peekNextPercentGenerated.xxLargeDesktop
      ? "1"
      : "0",
    ["--f-c-has-peek-next-xlarge"]: peekNextPercentGenerated.xLargeAndAbove
      ? "1"
      : "0",
    ["--f-c-has-peek-next-desktop"]: peekNextPercentGenerated.desktopAndAbove
      ? "1"
      : "0",
    ["--f-c-has-peek-next-tablet"]: peekNextPercentGenerated.tabletAndAbove
      ? "1"
      : "0",
    ["--f-c-has-peek-next-mobile"]: peekNextPercentGenerated.mobileAndAbove
      ? "1"
      : "0",
    // Has peek previous variables
    ["--f-c-has-peek-prev-xxlarge"]: peekPreviousPercentGenerated.xxLargeDesktop
      ? "1"
      : "0",
    ["--f-c-has-peek-prev-xlarge"]: peekPreviousPercentGenerated.xLargeAndAbove
      ? "1"
      : "0",
    ["--f-c-has-peek-prev-desktop"]:
      peekPreviousPercentGenerated.desktopAndAbove ? "1" : "0",
    ["--f-c-has-peek-prev-tablet"]: peekPreviousPercentGenerated.tabletAndAbove
      ? "1"
      : "0",
    ["--f-c-has-peek-prev-mobile"]: peekPreviousPercentGenerated.mobileAndAbove
      ? "1"
      : "0",
    // Loop variables
    ["--f-c-loop-xxlarge"]: loopGenerated.xxLargeDesktop ? "1" : "0",
    ["--f-c-loop-xlarge"]: loopGenerated.xLargeAndAbove ? "1" : "0",
    ["--f-c-loop-desktop"]: loopGenerated.desktopAndAbove ? "1" : "0",
    ["--f-c-loop-tablet"]: loopGenerated.tabletAndAbove ? "1" : "0",
    ["--f-c-loop-mobile"]: loopGenerated.mobileAndAbove ? "1" : "0",
  };

  return (
    <div
      ref={viewRef}
      className={cn(
        "f_c_carousel_wrapper_base",
        "f_c_carousel_wrapper_mobile",
        className,
        shouldShowPrevButton || shouldShowNextButton
          ? "f_c_show_arrow_buttons_mobile"
          : undefined
      )}
      style={vars}
    >
      {renderPreviousButton()}
      <div
        className="f_c_outer_container"
        ref={shouldAttachEmblaRef ? emblaRef : undefined}
        data-test-id="carousel-slides"
      >
        <Flex className="f_c_inner_container">
          {items.map((item, index) => {
            return (
              <div className={slideClassName} key={index} data-index={index}>
                {slideRenderFlags.has(index) || freeFlow
                  ? renderItem(item, index)
                  : null}
              </div>
            );
          })}
          {hasCTA && isNotUndefined(ctaTileData) ? (
            <div
              className={slideClassName}
              key="cta"
              data-index={items.length}
              style={{ alignSelf: "center" }}
            >
              {slideRenderFlags.size >= items.length ? (
                <CTATile key="cta-tile" data={ctaTileData} />
              ) : null}
            </div>
          ) : null}
        </Flex>
      </div>
      {renderNextButton()}
      {renderDots()}
    </div>
  );
};
